import { useSite } from "@/context/SiteContext";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, BadgeCheck, Flame, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export const ChefCard = ({ chef, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
  >
    <Link
      to={`/chef/${chef.id}`}
      data-testid={`chef-card-${chef.id}`}
      className="group block bg-white border border-neutral-200 hover:border-[#d00000] transition-colors duration-300"
    >
      <div className="relative overflow-hidden aspect-[4/5]">
        <img
          src={chef.img}
          alt={chef.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
        {chef.verified && (
          <span className="absolute top-4 left-4 flex items-center gap-1 bg-[#d00000] px-2.5 py-1 text-xs text-white">
            <BadgeCheck className="w-3.5 h-3.5" /> Verified
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <span className="text-xs uppercase tracking-[0.2em] text-neutral-200">{chef.cuisine}</span>
          <h3 className="font-display text-2xl font-bold text-white leading-tight">{chef.name}</h3>
          <p className="text-sm text-neutral-300">{chef.kitchen} · {chef.city}</p>
        </div>
      </div>
      <div className="p-5 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm text-neutral-900">
          <Star className="w-4 h-4 fill-[#f5a623] text-[#f5a623]" /> {chef.rating}
          <span className="text-neutral-400">({chef.reviews})</span>
        </span>
        <span className="text-sm text-[#d00000] group-hover:translate-x-1 transition-transform">View kitchen →</span>
      </div>
    </Link>
  </motion.div>
);

export const DishCard = ({ dish, index = 0 }) => {
  const { addItem } = useCart();
  const handleAdd = () => {
    addItem(dish);
    toast.success(`${dish.name} added to cart`);
  };
  return (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.05 }}
    className="group bg-white border border-neutral-200 hover:border-[#d00000] transition-colors duration-300 flex flex-col"
    data-testid={`dish-card-${dish.id}`}
  >
    <div className="relative overflow-hidden aspect-square">
      <img src={dish.img} alt={dish.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <span className="absolute top-3 right-3 bg-[#d00000] text-white text-sm font-semibold px-3 py-1">€{dish.price}</span>
      {dish.spicy > 0 && (
        <span className="absolute top-3 left-3 flex gap-0.5 bg-black/60 backdrop-blur px-2 py-1">
          {Array.from({ length: dish.spicy }).map((_, i) => (
            <Flame key={i} className="w-3 h-3 text-[#f5a623]" />
          ))}
        </span>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-[0.15em] text-neutral-400">{dish.cuisine}</span>
        <span className="flex items-center gap-1 text-xs text-neutral-900">
          <Star className="w-3 h-3 fill-[#f5a623] text-[#f5a623]" /> {dish.rating}
        </span>
      </div>
      <h3 className="font-display text-xl font-bold text-neutral-900 leading-tight mb-2">{dish.name}</h3>
      <p className="text-sm text-neutral-500 line-clamp-2 flex-1">{dish.desc}</p>
      <button
        onClick={handleAdd}
        data-testid={`add-to-cart-${dish.id}`}
        className="mt-4 flex items-center justify-center gap-2 w-full h-11 bg-neutral-900 text-white text-sm font-medium hover:bg-[#d00000] transition-colors"
      >
        <Plus className="w-4 h-4" /> Add to cart · €{dish.price}
      </button>
    </div>
  </motion.div>
  );
};

export const SectionHeader = ({ label, title, action }) => {
  const { t } = useSite();
  return (
  <div className="flex items-end justify-between mb-10 gap-6">
    <div>
      {label && <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">{t(label)}</p>}
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900">{t(title)}</h2>
    </div>
    {action}
  </div>
  );
};
